// Creating and changing pulls from the app.

import { SPEC } from "../../shared/catalog";
import { freeStreamKey } from "../../shared/ids";
import type { Pull, PullConfig, PullKind, Stream, WidgetItem } from "../../shared/types";
import { create } from "zustand";
import { api } from "../lib/api";
import { get, set, toast } from "./store";

export interface PullResult {
  ok: boolean;
  pull: Pull;
  stream: Stream | null;
  sample: unknown;
}

function absorb(r: PullResult) {
  set((s) => ({
    pulls: { ...s.pulls, [r.pull.id]: r.pull },
    streams: r.stream ? { ...s.streams, [r.stream.key]: r.stream } : s.streams,
  }));
}

export async function createPull(p: { kind: PullKind; name: string; stream: string; config: PullConfig; every: number }): Promise<PullResult> {
  const r = await api<PullResult>("/api/pulls", { body: p });
  absorb(r);
  if (r.pull.status === "error") toast({ level: "warn", title: `${p.name} could not read yet`, body: r.pull.error });
  return r;
}

export async function updatePull(id: string, p: Partial<Pull>): Promise<PullResult> {
  const r = await api<PullResult>(`/api/pulls/${encodeURIComponent(id)}`, { method: "PUT", body: p });
  absorb(r);
  return r;
}

/**
 * A widget's source read again now, by a person (Round 7): from its header, its corner, or a custom widget's own
 * board.refresh(). One read at a time, and none again for a few seconds after (a little sooner after a failed one), so
 * a run of clicks reads once and never runs up usage. Says so when a read fails.
 */
export const useRefreshing = create<Record<string, boolean>>(() => ({}));
const lastRefresh = new Map<string, number>();
const REFRESH_COOLDOWN = 20_000;
const RETRY_AFTER = 5_000;
export async function refreshNow(pullId: string): Promise<void> {
  if (useRefreshing.getState()[pullId] || Date.now() - (lastRefresh.get(pullId) ?? 0) < REFRESH_COOLDOWN) return;
  useRefreshing.setState({ [pullId]: true });
  let ok = false;
  try {
    const r = await runPull(pullId);
    ok = r.pull.status === "ok";
    if (!ok) toast({ level: "warn", title: "Could not refresh", body: r.pull.error });
  } catch (err) {
    toast({ level: "error", title: "Could not refresh", body: (err as Error).message });
  } finally {
    lastRefresh.set(pullId, ok ? Date.now() : Date.now() - REFRESH_COOLDOWN + RETRY_AFTER);
    useRefreshing.setState({ [pullId]: false });
  }
}

export async function runPull(id: string): Promise<PullResult> {
  const r = await api<PullResult>(`/api/pulls/${encodeURIComponent(id)}/run`, { body: {} });
  absorb(r);
  return r;
}

export async function deletePull(id: string) {
  await api(`/api/pulls/${encodeURIComponent(id)}`, { method: "DELETE" });
  set((s) => {
    const pulls = { ...s.pulls };
    delete pulls[id];
    return { pulls };
  });
}

export async function testPull(kind: PullKind, config: PullConfig, pullId?: string) {
  return api<{ ok: boolean; error: string | null; sample: unknown; input: Record<string, unknown> | null; status: number | null; ms: number | null }>("/api/pulls/test", { body: { kind, config, pullId } });
}

export async function pushValue(stream: string, body: Record<string, unknown>) {
  const r = await api<{ stream: Stream }>(`/api/streams/${encodeURIComponent(stream)}`, { body });
  set((s) => ({ streams: { ...s.streams, [r.stream.key]: r.stream } }));
  return r.stream;
}

/**
 * Where a number typed into a widget goes: the stream it reads, or, with none yet, one of its own named for it that no
 * stream, source or other widget has (shared/ids.ts freeStreamKey). Both ways of typing one (the tile's "Or type it in"
 * and Connect's "Type it in") ask here.
 */
export function typedStreamKey(item: WidgetItem): string {
  if (item.stream) return item.stream;
  const s = get();
  const used = (k: string) => Object.hasOwn(s.streams, k) || Object.values(s.pulls).some((p) => p.stream === k) || Object.values(s.items).some((i) => i.id !== item.id && i.kind === "widget" && i.stream === k);
  return freeStreamKey(item.title || SPEC[item.type].label, used);
}
